"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, CircleCheck as CheckCircle2, TrendingUp, Calendar, ArrowRight, ShieldCheck, Award, Search, Star } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const trustPoints = [{
  title: "100% Direct Access",
  desc: "You work directly with me, Yassine. Zero account managers or junior staff."
}, {
  title: "Google Map Pack Focus",
  desc: "Targeted Wigan local signals to drive phone calls and walk-in enquiries."
}, {
  title: "No Long Contracts",
  desc: "Transparent month-to-month strategy built on measurable SEO deliverables."
}];
const metrics = [{
  label: "Local Visibility Boost",
  value: "+240%"
}, {
  label: "Map Pack Top 3 Placement",
  value: "88%"
}, {
  label: "Direct Enquiries Growth",
  value: "3.5x"
}];
export default function Localhero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localheroauthority" data-nav="dark" className="relative min-h-[90vh] w-full bg-primary text-primary-foreground py-16 md:py-24 lg:py-28 overflow-hidden flex items-center">
      {/* Background Ambience */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-secondary/15 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.05)_0%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" variants={containerVariants} initial="hidden" animate="visible">
          {/* Main Hero Copy */}
          <div className="lg:col-span-7 space-y-8 text-left">
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3.5 py-1.5 text-xs sm:text-sm font-medium tracking-wide bg-secondary text-secondary-foreground rounded-full shadow-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 text-accent" />
                <span>Wigan &amp; Greater Manchester SEO Expert</span>
              </Badge>
              <span className="hidden sm:inline-flex items-center gap-1 text-xs text-primary-foreground/80 font-medium">
                <ShieldCheck className="w-4 h-4 text-accent" />
                Verified Results
              </span>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15]">
                Local SEO Consultant in Wigan
              </h2>
              <p className="font-sans text-lg sm:text-xl text-primary-foreground/90 max-w-2xl leading-relaxed">
                I help Wigan businesses outrank local competitors, dominate Google Map Pack results, and attract high-converting local customers. You work directly with me—<strong className="text-white font-semibold">Yassine Essaadi</strong>—with zero agency fluff, hidden fees, or middlemen.
              </p>
            </motion.div>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold px-8 py-6 rounded-lg shadow-lg transition-all" asChild>
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="w-5 h-5 mr-2" /></SafeHydrate>
                  Book a Free SEO Consultation
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 text-base font-medium px-8 py-6 rounded-lg transition-all" asChild>
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </motion.div>

            {/* Quick Pillars */}
            <motion.div variants={itemVariants} className="pt-6 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {trustPoints.map((point, i) => <div key={i} data-index={i} className="flex flex-col space-y-1">
                  <div className="flex items-center gap-2 font-serif font-semibold text-sm text-primary-foreground">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                    <span>{point.title}</span>
                  </div>
                  <p className="text-xs text-primary-foreground/80 leading-snug">
                    {point.desc}
                  </p>
                </div>)}
            </motion.div>
          </div>

          {/* Side Card / Visual Authority */}
          <motion.div variants={itemVariants} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Consultant Card */}
              <div className="bg-card text-card-foreground rounded-2xl p-6 sm:p-8 shadow-2xl border border-border/80 relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_wigan_seo.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_wigan_seo.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_wigan_seo.png-wvc-sizes" alt="Yassine Essaadi - Independent Local SEO Consultant in Wigan" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-primary shadow-md" loading="lazy" />
                    <span className="absolute bottom-0 right-0 w-5 h-5 bg-emerald-500 border-2 border-card rounded-full" title="Available for Wigan Consultations" />
                  </div>
                  <div>
                    <h2 className="font-serif text-xl sm:text-2xl font-bold text-card-foreground">
                      Yassine Essaadi
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground font-medium">
                      Independent SEO Strategy Consultant
                    </p>
                    <div className="flex items-center gap-1 mt-1 text-amber-500 text-xs font-semibold">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <span className="text-card-foreground text-xs ml-1">5.0 Wigan Client Rating</span>
                    </div>
                  </div>
                </div>

                {/* Local Search Mockup */}
                <div className="bg-muted p-4 rounded-xl border border-border mb-6 space-y-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Search className="w-3.5 h-3.5 text-primary" />
                      Google Search: Wigan
                    </span>
                    <span className="text-emerald-600 font-bold bg-emerald-500/10 px-2 py-0.5 rounded text-[13px]">
                      #1 Local Rank
                    </span>
                  </div>
                  <div className="bg-background p-3 rounded-lg border border-border shadow-xs space-y-1">
                    <div className="text-xs font-medium text-primary flex items-center justify-between">
                      <span>Your Local Wigan Business</span>
                      <Award className="w-3.5 h-3.5 text-accent" />
                    </div>
                    <p className="text-[13px] text-muted-foreground">
                      Top Map Pack Position + High-Intent Keywords
                    </p>
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border text-center">
                  {metrics.map((m, idx) => <div key={idx} data-index={idx} className="p-2 rounded-lg bg-muted/60">
                      <div className="font-serif font-bold text-base sm:text-lg text-primary">
                        {m.value}
                      </div>
                      <div className="text-[12px] sm:text-xs text-muted-foreground font-medium leading-tight mt-0.5">
                        {m.label}
                      </div>
                    </div>)}
                </div>

                {/* Direct Action Link */}
                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Based in Swindon &amp; serving Wigan</span>
                  <Link to="/ContactPage" className="text-primary font-semibold hover:underline flex items-center gap-1">
                    Get In Touch
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-5 -right-5 bg-accent text-accent-foreground px-4 py-2.5 rounded-xl text-xs font-semibold shadow-xl border border-accent-foreground/10 hidden sm:flex items-center gap-2 z-20">
                <TrendingUp className="w-4 h-4" />
                <span>Tailored Wigan Growth Strategy</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}